import type { EventBasedMarketView, MeetingBasedMarketView } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';
import type {
  AllMarketGroup,
  ClientMarketGroup,
  CuratedMarketGroup
} from '@swf/sports/types/marketGroups';
import { buildSlug } from '@swf/sports/utils/build-slug';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';

import { TXTWCL_All_Markets } from './useDisplayableMarketGroups.messages.json';

interface BuildClientMarketGroupParams {
  marketGroup: CuratedMarketGroup | AllMarketGroup;
  allMarketsMessage: string;
  locale: string;
}

const buildClientMarketGroup = ({
  marketGroup,
  allMarketsMessage,
  locale
}: BuildClientMarketGroupParams): ClientMarketGroup => {
  if (marketGroup.type === 'ALL_MARKETS') {
    return {
      ...marketGroup,
      name: allMarketsMessage,
      slug: buildSlug(allMarketsMessage, locale)
    };
  } else {
    return marketGroup;
  }
};

const useDisplayableMarketGroups = ({
  markets,
  groups,
  currentMarketGroupSlug
}: {
  markets: Partial<Record<string, EventBasedMarketView | MeetingBasedMarketView>>;
  groups: (CuratedMarketGroup | AllMarketGroup)[];
  currentMarketGroupSlug: string;
}): [ClientMarketGroup[], ClientMarketGroup | undefined] => {
  const { formatMessage } = useRegulatoryIntl();
  const locale = useSelector(select.locale.get);
  const allMarketsMessage = formatMessage(TXTWCL_All_Markets);

  const displayableMarketGroups: ClientMarketGroup[] = useMemo(
    () =>
      groups.filter(notEmpty).reduce<ClientMarketGroup[]>((acc, marketGroup) => {
        const templatesWithMarkets = marketGroup.templates.filter((template) => {
          const templateMarketIds = isCompoundMarketTemplate(template)
            ? template.marketIds.filter(notEmpty)
            : [template.marketId];
          return templateMarketIds.some((templateMarketId) => markets[templateMarketId]);
        });

        const clientMarketGroup = buildClientMarketGroup({
          marketGroup,
          allMarketsMessage,
          locale
        });

        if (
          (clientMarketGroup.slug && clientMarketGroup.slug === currentMarketGroupSlug) ||
          templatesWithMarkets.length
        ) {
          acc.push({ ...clientMarketGroup, templates: templatesWithMarkets });
        }

        return acc;
      }, []),
    [allMarketsMessage, currentMarketGroupSlug, groups, markets, locale]
  );

  const currentMarketGroup = !currentMarketGroupSlug
    ? displayableMarketGroups[0]
    : displayableMarketGroups.find(({ slug }) => slug === currentMarketGroupSlug);

  return [displayableMarketGroups, currentMarketGroup];
};

export default useDisplayableMarketGroups;
